import { Link } from 'react-router-dom';
import { Block, ErrorNote, Eyebrow, Loading, Screen } from '../components/primitives';
import { ProLock } from '../components/pro-lock';
import { apiErrorPayload } from '../lib/api';
import { useFeatureLocked, useKarma } from '../lib/queries';

/**
 * The karma reading: what the member falls back on, what they are growing
 * toward, where patience is asked, and one practice for it.
 *
 * There is no score on this screen. The prototype had the model invent one out
 * of ten, and a made-up number reads as a measurement.
 */
export function KarmaPage() {
  const locked = useFeatureLocked('karma');
  const karma = useKarma();

  if (locked) {
    return (
      <Screen back="/align" eyebrow="Karma" title="Your karma reading">
        <ProLock feature="karma" />
      </Screen>
    );
  }
  if (karma.isPending) return <Loading label="Reading your chart" />;
  if (karma.isError) {
    if (apiErrorPayload(karma.error)?.error === 'birth_details_required') {
      return (
        <Screen back="/align" eyebrow="Karma" title="Add your birth details">
          <Block
            action={
              <Link to="/settings" className="eyebrow underline underline-offset-4 hover:text-ink">
                Go to settings
              </Link>
            }
          >
            This reading comes from your own chart, so it needs your birth date and place.
          </Block>
        </Screen>
      );
    }
    return <ErrorNote error={karma.error} retry={() => void karma.refetch()} />;
  }

  const k = karma.data.karma;
  return (
    <Screen
      back="/align"
      eyebrow={k.dasha ? `${k.dasha.maha} period · ${k.dasha.antar} sub-period` : 'Karma'}
      title="What you keep coming back to"
      subtitle={k.lesson}
    >
      <section className="border-b border-border px-5 py-6">
        <Eyebrow className="mb-2">What you fall back on</Eyebrow>
        <p className="text-[0.95rem] leading-relaxed text-ink/80">{k.pattern}</p>
        <p className="eyebrow mt-3">
          Ketu in house {k.ketu.house}, {k.ketu.sign}
        </p>
      </section>

      <section className="border-b border-border px-5 py-6">
        <Eyebrow className="mb-2">What you are growing toward</Eyebrow>
        <p className="text-[0.95rem] leading-relaxed text-ink/80">{k.path}</p>
        <p className="eyebrow mt-3">
          Rahu in house {k.rahu.house}, {k.rahu.sign}
        </p>
      </section>

      <section className="border-b border-border px-5 py-6">
        <Eyebrow className="mb-2">Where patience is asked</Eyebrow>
        <p className="text-[0.95rem] leading-relaxed text-ink/80">{k.patience}</p>
        <p className="eyebrow mt-3">Saturn in house {k.saturn.house}</p>
      </section>

      <section className="border-b border-border px-5 py-6">
        <Eyebrow className="mb-2">A practice for it</Eyebrow>
        <h2 className="font-display text-[1.2rem] font-semibold leading-snug text-ink">
          {k.cleansing.name}
        </h2>
        <p className="mt-1.5 text-sm leading-relaxed text-ink/70">{k.cleansing.why}</p>
        <ol className="mt-5 space-y-4">
          {k.cleansing.steps.map((step, i) => (
            <li key={i} className="flex gap-3.5">
              <span className="eyebrow w-6 shrink-0 tabular-nums">{i + 1}</span>
              <span className="flex-1 text-[0.95rem] leading-relaxed text-ink/70">{step}</span>
            </li>
          ))}
        </ol>
      </section>

      <section className="border-b border-border px-5 py-6">
        <Eyebrow className="mb-4">Traditional correspondences</Eyebrow>
        <dl className="grid grid-cols-2 gap-x-6 gap-y-5">
          <div>
            <dt className="eyebrow">Day</dt>
            <dd className="mt-1 text-[0.95rem] text-ink">{k.lucky.day}</dd>
          </div>
          <div>
            <dt className="eyebrow">Stone</dt>
            <dd className="mt-1 text-[0.95rem] text-ink">{k.lucky.gem}</dd>
          </div>
          <div>
            <dt className="eyebrow">Numbers</dt>
            <dd className="mt-1 font-mono text-[0.95rem] tabular-nums text-ink">
              {k.lucky.numbers.join(' · ')}
            </dd>
          </div>
          <div>
            <dt className="eyebrow">Colours</dt>
            <dd className="mt-1.5 flex flex-wrap items-center gap-2.5">
              {k.lucky.colors.map((c) => (
                <span key={c.hex} className="flex items-center gap-1.5">
                  <span
                    className="h-5 w-5 shrink-0 rounded-full ring-1 ring-inset ring-[rgba(16,32,26,0.22)]"
                    style={{ backgroundColor: c.hex }}
                    aria-hidden
                  />
                  <span className="text-[0.95rem] text-ink">{c.name}</span>
                </span>
              ))}
            </dd>
          </div>
        </dl>
      </section>

      <p className="px-5 py-6 text-xs leading-relaxed text-muted-foreground">
        {k.reference === 'moon' &&
          'Your birth time is not on file, so the houses here are counted from your natal Moon rather than your rising sign. '}
        {k.disclaimer}
      </p>
    </Screen>
  );
}
